import { useEffect, useState } from 'react';
import { useGallery } from '../hooks/useGallery';
import { useGallerySelection } from '../hooks/useGallerySelection';
import { usePendingMediaPoller } from '../hooks/usePendingMediaPoller';
import { useMediaDownload } from '../hooks/useMediaDownload';
import type { DownloadableItem } from '../hooks/useMediaDownload';
import { playAudio, stopAudio, useAudioPlayer } from '../hooks/useAudioPlayer';
import { claimMediaFocus } from '../hooks/mediaFocus';
import { GalleryToolbar } from './GalleryToolbar';
import { NewMusicDialog } from './NewMusicDialog';
import { LoadMoreSentinel } from './LoadMoreSentinel';
import { apiJson } from '../api/client';

const API_BASE = import.meta.env.VITE_API_BASE as string;
const ENDPOINT = '/api/tools/music';

interface MusicItem {
  id: string;
  title: string;
  prompt: string;
  format: string;
  has_vocals: number;
  koji_codename: string;
  created_at: number;
  r2_key: string | null;
  signedUrl: string | null;
  // Stage 25: async job state.
  status: 'submitted' | 'in_progress' | 'ready' | 'failed' | string;
  error_message: string | null;
  completed_at: number | null;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'submitted':
      return 'Queued';
    case 'in_progress':
      return 'Generating…';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function MusicGallery() {
  const { items, loading, error, deleteItem, refresh, loadMore, hasMore } =
    useGallery<MusicItem>(ENDPOINT);
  const selection = useGallerySelection(ENDPOINT);
  const download = useMediaDownload({
    zipEndpoint: '/api/tools/music/zip',
    kind: 'music',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  // Stage 37: retry state mirrors ImageGallery's pattern.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  // QA Stage: track which card initiated the currently-playing audio
  // so we can render a Stop button + green outline on the active row.
  // The shared audio bus is single-element so at most one track can be
  // active at a time; whichever card last clicked Play "owns" playingId
  // until the bus emits playing=false (natural end, manual stop, or
  // another surface taking over).
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audio = useAudioPlayer();

  usePendingMediaPoller('/api/tools/music/pending', refresh);

  useEffect(() => {
    if (!audio.playing) setPlayingId(null);
  }, [audio.playing]);

  async function retryMusic(id: string): Promise<void> {
    setRetryError(null);
    setRetryingId(id);
    try {
      await apiJson<{ retried: string; handler: string }>(
        'POST',
        `/api/tools/music/${encodeURIComponent(id)}/retry`,
      );
      // Refresh now so the card flips to "Queued" immediately. The
      // pending poller picks up subsequent state transitions
      // automatically.
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRetryError(`Retry failed for ${id}: ${msg}`);
    } finally {
      setRetryingId((current) => (current === id ? null : current));
    }
  }

  const allIds = items.map((i) => i.id);

  // Stage 27: map the currently-selected tracks into DownloadableItem shape.
  // Music rows store a `format` column ('mp3' | 'wav' | 'ogg'); pass it
  // through as `mime_type` so useMediaDownload derives the right extension.
  const selectedDownloadItems: DownloadableItem[] = items
    .filter((t) => selection.selected.has(t.id))
    .map((t) => ({
      id: t.id,
      signedUrl: t.signedUrl,
      status: t.status,
      title: t.title,
      koji_codename: t.koji_codename,
      mime_type: (t.format || 'wav').toLowerCase(),
    }));

  return (
    <section className="gallery gallery-music">
      <GalleryToolbar
        onNew={() => setDialogOpen(true)}
        newLabel="+ New track"
        selection={selection}
        allIds={allIds}
        download={{
          selectedItems: selectedDownloadItems,
          downloadOne: download.downloadOne,
          downloadZip: download.downloadZip,
          busy: download.busy,
          error: download.error,
        }}
      />
      {retryError ? (
        <div className="status-missing small" role="alert">
          {retryError}
        </div>
      ) : null}
      {loading && items.length === 0 ? (
        <div className="muted">Loading tracks…</div>
      ) : error ? (
        <div className="status-missing">Error: {error}</div>
      ) : items.length === 0 ? (
        <p className="muted gallery-empty">No generated tracks yet.</p>
      ) : (
        <ul className="track-list">
          {items.map((t) => {
            const isSelected = selection.selected.has(t.id);
            const isReady = t.status === 'ready' && t.signedUrl;
            const isFailed = t.status === 'failed';
            const isPending =
              t.status === 'submitted' || t.status === 'in_progress';
            return (
              <li
                key={t.id}
                className={
                  'track-row' +
                  (isSelected ? ' track-row-selected' : '') +
                  (isPending ? ' gallery-card-pending' : '') +
                  (isFailed ? ' gallery-card-failed' : '') +
                  (playingId === t.id ? ' track-row-playing' : '')
                }
              >
                {selection.selectMode ? (
                  <label className="gallery-select-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => selection.toggleSelected(t.id)}
                      aria-label={`Select ${t.title || t.koji_codename}`}
                    />
                  </label>
                ) : isReady ? (
                  playingId === t.id ? (
                    <button
                      className="btn-play btn-stop"
                      onClick={() => {
                        stopAudio();
                        setPlayingId(null);
                      }}
                      aria-label={`Stop ${t.title}`}
                      title="Stop"
                    >
                      ■
                    </button>
                  ) : (
                    <button
                      className="btn-play"
                      onClick={() => {
                        claimMediaFocus('music');
                        setPlayingId(t.id);
                        void playAudio(absolute(t.signedUrl as string));
                      }}
                      aria-label={`Play ${t.title}`}
                      title="Play"
                    >
                      ▶
                    </button>
                  )
                ) : (
                  <div className="gallery-status-badge" aria-live="polite">
                    {statusLabel(t.status)}
                  </div>
                )}
                <div className="track-meta">
                  <div className="track-title">{t.title || t.koji_codename}</div>
                  <div className="muted small" title={t.prompt}>
                    {(t.format || '').toUpperCase()} ·{' '}
                    {t.has_vocals ? 'with vocals' : 'instrumental'} ·{' '}
                    {t.koji_codename}
                  </div>
                  {isPending ? (
                    <div className="muted small">~90 s</div>
                  ) : null}
                  {isFailed && t.error_message ? (
                    <div
                      className="status-missing small"
                      title={t.error_message}
                    >
                      {t.error_message.slice(0, 80)}
                      {t.error_message.length > 80 ? '…' : ''}
                    </div>
                  ) : null}
                </div>
                {!selection.selectMode ? (
                  <div className="gallery-card-actions">
                    {isFailed ? (
                      <button
                        className="btn-secondary small"
                        disabled={retryingId === t.id}
                        onClick={() => void retryMusic(t.id)}
                        title="Re-queue this track with the original prompt"
                      >
                        {retryingId === t.id ? 'Retrying…' : '↻ Retry'}
                      </button>
                    ) : null}
                    <button
                      className="btn-danger small"
                      onClick={() => {
                        if (confirm('Delete this track?'))
                          void deleteItem(t.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <LoadMoreSentinel
        hasMore={hasMore}
        loading={loading}
        onReach={() => void loadMore()}
      />
      <NewMusicDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => void refresh()}
      />
    </section>
  );
}
